import React, {useEffect, useMemo, useRef, useState} from 'react';
import {inject, observer} from 'mobx-react';
import {useLocation, useNavigate, useParams} from 'react-router-dom';
import {
  readChunkMap,
  storeChunkMap,
} from '../../Microreels/List/microreelChunkMap';
import {GeneratedVideoChunk} from '../../../data/models/LibraryFile/GeneratedVideoChunk';
import VideoEditorUI from '../view/VideoEditorUI';
import LibraryRepository, {
  EditChunkOption,
} from '../../../data/repositories/LibraryRepository';
import VideoEditorStore, {VideoEditorState} from '../store/VideoEditorStore';
import {toastConfig} from '../../../uiToolkit/Toast/toastConfig';
import {toast} from 'react-toastify';
import {BrowseStoryblocks} from '../../../modules/BrowseStoryblocks';

import MyLibraryModalStore from '../../../modules/MyLibraryModal/store/MyLibraryModalStore';
import {
  MediaFile,
  MEDIA_FILES_ALL_VIDEO,
} from '../../../data/models/LibraryFile/MediaFile';

import {BrowseMediaLibrary} from '../../../modules/BrowseMediaLibrary/';
import EditNarrationModal from '../components/EditNarrationModal';
import ReplaceAIMediaModal from '../components/ReplaceAIMediaModal';
import EditMediaModal from '../components/EditMediaModal';
import {SaveArtifact} from '../components/editMediaTypes';
import ConfirmationModal from '../../../uiToolkit/ConfirmationModal';
import {VideoEditorText} from '../VideoEditorText';
import CropVideoStepUI from '../../../modules/Editor/AddVideoSourceWizard/view/CropVideoStepUI';
import StoryblocksStore from '../../../modules/BrowseStoryblocks/store/StoryblocksStore';

interface Props {
  videoEditorStore?: VideoEditorStore;
  myLibraryModalStore?: MyLibraryModalStore;
  storyblocksStore?: StoryblocksStore;
}

const EditVideoSourceContainer = inject(
  'videoEditorStore',
  'myLibraryModalStore',
  'storyblocksStore',
)(
  observer(
    ({videoEditorStore, myLibraryModalStore, storyblocksStore}: Props) => {
      const [chunks, setChunks] = useState<GeneratedVideoChunk[]>([]);
      const [selectedChunk, setSelectedChunk] = useState<
        GeneratedVideoChunk | undefined
      >();
      const [
        showBrowseStoryblocksForChunkId,
        setShowBrowseStoryblocksForChunkId,
      ] = useState<string | null>(null);

      const [showBrowseLibraryForChunkId, setShowBrowseLibraryForChunkId] =
        useState<string | null>(null);
      const [replaceAIMediaChunk, setReplaceAIMediaChunk] =
        useState<GeneratedVideoChunk | null>(null);
      const [isReplaceAIMediaSaving, setIsReplaceAIMediaSaving] =
        useState(false);
      const [editMediaChunk, setEditMediaChunk] =
        useState<GeneratedVideoChunk | null>(null);
      const [isEditMediaSaving, setIsEditMediaSaving] = useState(false);
      const [targetChunkId, setTargetChunkId] = useState<string | null>(null);
      const [libraryDurationFilter, setLibraryDurationFilter] = useState<
        number | null
      >(null);
      const [showAddLibrary, setShowAddLibrary] = useState(false);
      const [pendingAddSourceUrl, setPendingAddSourceUrl] = useState<
        string | null
      >(null);
      const [pendingAddRemoveAudio, setPendingAddRemoveAudio] = useState(false);
      const [pendingAddLibraryMediaFileId, setPendingAddLibraryMediaFileId] =
        useState<string | null>(null);
      const [showAddStoryblocks, setShowAddStoryblocks] = useState(false);
      const [pendingAddStoryblocksVideoId, setPendingAddStoryblocksVideoId] =
        useState<number | null>(null);
      const [newChunkIds, setNewChunkIds] = useState<Set<string>>(new Set());
      const previousChunkIdsRef = useRef<Set<string>>(new Set());
      const markNewAfterFetchRef = useRef(false);
      const [forceWorking, setForceWorking] = useState(false);
      const forceWorkingUntilRef = useRef(0);
      const pendingDeleteIdRef = useRef<string | null>(null);

      // Edit narration modal state
      const [editNarrationChunkId, setEditNarrationChunkId] = useState<
        string | null
      >(null);
      const [editNarrationText, setEditNarrationText] = useState('');
      const [isAddChunkPending, setIsAddChunkPending] = useState(false);
      const [isNarrationLoading, setIsNarrationLoading] = useState(false);
      const [isNarrationSaving, setIsNarrationSaving] = useState(false);
      const [showRemoveBgMusicConfirm, setShowRemoveBgMusicConfirm] =
        useState(false);

      const {organizationId, mediaFileId} = useParams();
      const navigate = useNavigate();
      const location = useLocation();

      const scopeState = location.state as {
        fromMicroreels?: boolean;
        microreelGroupId?: string;
        scopeReelId?: string;
        scopeTitle?: string;
        scopeUrls?: string[];
        scopeStart?: number;
        scopeEnd?: number;
      } | null;

      // Preferred lock source: the persistent chunk→reel map (stable ids,
      // survives reloads and duration drift from edits). Loaded once; chunks
      // that appear later (children from a replace) belong to the reel being
      // edited — locked chunks cannot produce children — and are appended to
      // the stored map so future sessions know them.
      const chunkMapRef = useRef<ReturnType<typeof readChunkMap>>();
      const chunkMapLoadedRef = useRef(false);

      // Fallback: the scope heuristic runs once against the initial chunks
      // and the result is frozen by chunk id — recomputing against the
      // original scope window after edits would shift the locks onto other
      // reels' chunks.
      const frozenLockedIdsRef = useRef<Set<string>>();

      const lockedChunkIds = useMemo(() => {
        if (!chunkMapLoadedRef.current) {
          chunkMapLoadedRef.current = true;
          chunkMapRef.current =
            scopeState?.fromMicroreels && scopeState.scopeReelId
              ? readChunkMap(mediaFileId)
              : undefined;
        }
        const map = chunkMapRef.current;
        const scopeReelId = scopeState?.scopeReelId;
        if (
          map &&
          scopeReelId &&
          Object.values(map.byChunk).some(reelId => reelId === scopeReelId)
        ) {
          const locked = new Set<string>();
          let mapChanged = false;
          chunks.forEach(chunk => {
            const owner = map.byChunk[chunk.id];
            if (owner === undefined) {
              if (chunk.include) {
                map.byChunk[chunk.id] = scopeReelId;
                mapChanged = true;
              }
              return;
            }
            if (owner !== scopeReelId) {
              locked.add(chunk.id);
            }
          });
          if (mapChanged && mediaFileId) {
            storeChunkMap(mediaFileId, map);
          }
          return locked;
        }
        if (frozenLockedIdsRef.current) {
          return frozenLockedIdsRef.current;
        }
        const locked = new Set<string>();
        if (!scopeState?.scopeTitle) return locked;
        const toPath = (url?: string) => {
          if (!url) return '';
          const noQuery = url.split('?')[0];
          try {
            return new URL(noQuery).pathname.toLowerCase();
          } catch {
            return noQuery.toLowerCase();
          }
        };
        const scopeUrlSet = new Set(
          (scopeState.scopeUrls ?? []).map(toPath).filter(Boolean),
        );
        const inScope = new Set<string>();
        chunks.forEach(chunk => {
          const urls = [
            toPath(chunk.uri),
            ...(chunk.artifacts ?? []).flatMap(artifact => [
              toPath(artifact.uri),
              toPath(artifact.publicUri),
            ]),
          ];
          if (urls.some(url => url && scopeUrlSet.has(url))) {
            inScope.add(chunk.id);
          }
        });
        const hasWindow =
          scopeState.scopeStart !== undefined &&
          scopeState.scopeEnd !== undefined &&
          scopeState.scopeEnd > scopeState.scopeStart;
        if (scopeUrlSet.size === 1 && hasWindow) {
          let cursor = 0;
          chunks.forEach(chunk => {
            if (!chunk.include) return;
            const center = cursor + chunk.duration / 2;
            if (
              center >= scopeState.scopeStart! - 0.5 &&
              center <= scopeState.scopeEnd! + 0.5
            ) {
              inScope.add(chunk.id);
            }
            cursor += chunk.duration;
          });
        }
        if (!inScope.size) return locked;
        chunks.forEach(chunk => {
          if (!inScope.has(chunk.id)) locked.add(chunk.id);
        });
        if (chunks.length) {
          frozenLockedIdsRef.current = locked;
        }
        return locked;
      }, [chunks, scopeState]);

      const scopeActive = !!scopeState?.scopeTitle && lockedChunkIds.size > 0;

      useEffect(() => {
        if (!lockedChunkIds.size) return;
        if (selectedChunk && !lockedChunkIds.has(selectedChunk.id)) return;
        const firstInScope = chunks.find(
          chunk => chunk.include && !lockedChunkIds.has(chunk.id),
        );
        if (firstInScope && firstInScope.id !== selectedChunk?.id) {
          setSelectedChunk(firstInScope);
        }
      }, [chunks, lockedChunkIds, selectedChunk?.id]);

      // Appending clips is never allowed when editing a microreel's video:
      // new footage would land outside the reel's segment (or shift the
      // chunks that belong to the other reels).
      const scopeAddDisabled = !!scopeState?.fromMicroreels;

      useEffect(() => {
        fetchChunks();
        if (
          videoEditorStore!.state === VideoEditorState.Idle &&
          !videoEditorStore!.isEditingTemporaryDisabled &&
          !forceWorking
        ) {
          return;
        }
        const interval = setInterval(() => {
          fetchChunks();
        }, 10000);
        return () => clearInterval(interval);
      }, [
        mediaFileId,
        videoEditorStore!.state,
        videoEditorStore!.isEditingTemporaryDisabled,
        forceWorking,
      ]);

      const fetchChunks = () => {
        if (organizationId && mediaFileId) {
          return videoEditorStore!
            .getVideoChunks(organizationId, mediaFileId)
            .then(chs => {
              const sorted = chs.sort((x, y) => (x.order > y.order ? 1 : -1));
              setChunks(sorted);
              selectFirstOrNextChunkIfSelectedDeleted(sorted);

              const currentIds = new Set(sorted.map(c => c.id));
              if (markNewAfterFetchRef.current) {
                const prevIds = previousChunkIdsRef.current;
                const added = new Set<string>();
                currentIds.forEach(id => {
                  if (!prevIds.has(id)) added.add(id);
                });
                if (added.size > 0) {
                  setNewChunkIds(prev => {
                    const next = new Set<string>();
                    prev.forEach(id => next.add(id));
                    added.forEach(id => next.add(id));
                    return next;
                  });
                  setIsAddChunkPending(false);
                  setForceWorking(false);
                  forceWorkingUntilRef.current = 0;
                }
                markNewAfterFetchRef.current = false;
              }

              if (
                pendingDeleteIdRef.current &&
                !currentIds.has(pendingDeleteIdRef.current)
              ) {
                pendingDeleteIdRef.current = null;
                setForceWorking(false);
                forceWorkingUntilRef.current = 0;
              }

              previousChunkIdsRef.current = currentIds;

              if (!videoEditorStore!.hasUnsavedChanges) {
                setNewChunkIds(new Set());
              }

              if (
                forceWorkingUntilRef.current &&
                Date.now() > forceWorkingUntilRef.current &&
                videoEditorStore!.state === VideoEditorState.Idle &&
                !videoEditorStore!.isEditingTemporaryDisabled &&
                !isAddChunkPending
              ) {
                setForceWorking(false);
                forceWorkingUntilRef.current = 0;
                pendingDeleteIdRef.current = null;
              }
            })
            .catch((error: Error) => {
              toast.error(error.message, toastConfig);
            });
        }
        return Promise.resolve();
      };

      // Helper to extract base URL without query parameters
      const getBaseUrl = (url: string | undefined): string => {
        if (!url) return '';
        const idx = url.indexOf('?');
        return idx === -1 ? url : url.substring(0, idx);
      };

      const selectFirstOrNextChunkIfSelectedDeleted = (
        updated: GeneratedVideoChunk[],
      ) => {
        if (selectedChunk) {
          const currentChunk = updated.find(x => x.id === selectedChunk.id);

          if (!currentChunk) {
            // Chunk was deleted - select next or first
            const idx = chunks.findIndex(x => x.id === selectedChunk.id);
            const nextIncluded = updated.slice(idx + 1).find(x => x.include);
            const firstIncluded = updated.find(x => x.include);
            setSelectedChunk(nextIncluded || firstIncluded || undefined);
          } else if (!currentChunk.include) {
            // Chunk is excluded - select next or first included
            const idx = updated.findIndex(x => x.id === selectedChunk.id);
            const nextIncluded = updated.slice(idx + 1).find(x => x.include);
            const firstIncluded = updated.find(x => x.include);
            setSelectedChunk(nextIncluded || firstIncluded || undefined);
          } else {
            // Chunk still exists and is included - update only if URI changed
            const oldBaseUri = getBaseUrl(selectedChunk.uri);
            const newBaseUri = getBaseUrl(currentChunk.uri);
            if (oldBaseUri !== newBaseUri) {
              setSelectedChunk(currentChunk);
            }
          }
        } else if (updated.length > 0) {
          const first = updated.find(x => x.include);
          if (first) setSelectedChunk(first);
        }
      };

      const onBack = () => {
        if (scopeState?.fromMicroreels) {
          navigate(
            scopeState.microreelGroupId
              ? `/manage/${organizationId}/microreels/${scopeState.microreelGroupId}`
              : `/manage/${organizationId}/microreels`,
          );
          return;
        }
        navigate(`/manage/${organizationId}/library`);
      };

      const openLibraryModal = () => {
        if (!myLibraryModalStore) return;

        (myLibraryModalStore as any).setAllowedTypes?.(MEDIA_FILES_ALL_VIDEO);
        (myLibraryModalStore as any).setOnConfirm?.(onLibraryConfirm);
        (myLibraryModalStore as any).setOnCancel?.(onLibraryCancel);

        if ((myLibraryModalStore as any).open)
          (myLibraryModalStore as any).open({multi: false});
        else if ((myLibraryModalStore as any).show)
          (myLibraryModalStore as any).show({multi: false});
        else (myLibraryModalStore as any).setIsOpen?.(true);
      };

      const closeLibraryModal = () => {
        if (!myLibraryModalStore) return;
        (myLibraryModalStore as any).close?.();
        (myLibraryModalStore as any).hide?.();
        (myLibraryModalStore as any).setIsOpen?.(false);
      };

      const onLibraryConfirm = async (selected: MediaFile | MediaFile[]) => {
        try {
          if (!organizationId || !mediaFileId || !targetChunkId) return;
          const picked: MediaFile = Array.isArray(selected)
            ? selected[0]
            : selected;

          const ok = await videoEditorStore!.EditChunk(
            organizationId,
            mediaFileId,
            EditChunkOption.ChangeFromLibrary,
            targetChunkId,
            {libraryMediaFileId: picked.id},
          );

          if (ok) await fetchChunks();
        } finally {
          closeLibraryModal();
          setShowBrowseLibraryForChunkId(null);
          setTargetChunkId(null);
          setLibraryDurationFilter(null);
        }
      };

      const onLibraryCancel = () => {
        closeLibraryModal();
        setShowBrowseLibraryForChunkId(null);
        setTargetChunkId(null);
        setLibraryDurationFilter(null);
      };

      const onEditChunk = async (option: EditChunkOption, chunkId: string) => {
        if (!organizationId || !mediaFileId) return;
        if (lockedChunkIds.has(chunkId)) return;
        const chunkToEdit = chunks.find(c => c.id === chunkId);
        if (
          chunkToEdit?.origin !== 1 &&
          option !== EditChunkOption.Delete &&
          option !== EditChunkOption.Reorder &&
          option !== EditChunkOption.EditNarration &&
          option !== EditChunkOption.ChangeMedia &&
          option !== EditChunkOption.ChangeFromLibrary
        ) {
          return;
        }

        // Block new operations if another operation is in progress
        if (
          videoEditorStore!.state !== VideoEditorState.Idle ||
          videoEditorStore!.isEditingTemporaryDisabled
        ) {
          return;
        }

        const shouldDelayWorkingUntilConfirm =
          option === EditChunkOption.ChangeMedia ||
          option === EditChunkOption.ChangeFromLibrary ||
          option === EditChunkOption.ReplaceAIMedia ||
          option === EditChunkOption.EditMedia ||
          option === EditChunkOption.EditNarration;

        if (
          !shouldDelayWorkingUntilConfirm &&
          option !== EditChunkOption.Reorder
        ) {
          setForceWorking(true);
          forceWorkingUntilRef.current = Date.now() + 20000;
          if (option === EditChunkOption.Delete) {
            pendingDeleteIdRef.current = chunkId;
          }
        }

        if (option === EditChunkOption.ChangeMedia) {
          setShowBrowseStoryblocksForChunkId(chunkId);
          return;
        }

        if (option === EditChunkOption.ChangeFromLibrary) {
          const chunkToEdit = chunks.find(c => c.id === chunkId);
          setLibraryDurationFilter(chunkToEdit?.duration ?? null);
          setTargetChunkId(chunkId);
          setShowBrowseLibraryForChunkId(chunkId);
          openLibraryModal();
          return;
        }

        if (option === EditChunkOption.ReplaceAIMedia) {
          const chunkToEdit = chunks.find(c => c.id === chunkId);
          if (chunkToEdit) setReplaceAIMediaChunk(chunkToEdit);
          return;
        }

        if (option === EditChunkOption.EditMedia) {
          const chunkToEdit = chunks.find(c => c.id === chunkId);
          if (chunkToEdit) setEditMediaChunk(chunkToEdit);
          return;
        }

        if (option === EditChunkOption.EditNarration) {
          setEditNarrationChunkId(chunkId);
          setIsNarrationLoading(true);

          try {
            const libraryRepository = new LibraryRepository();
            const response = await libraryRepository.getChunkNarration(
              organizationId,
              mediaFileId,
              chunkId,
            );
            if (!response.success || response.data === undefined) {
              throw new Error(VideoEditorText.editNarrationLoadFailed);
            }
            setEditNarrationText(response.data);
          } catch (error) {
            toast.error(VideoEditorText.editNarrationLoadFailed, toastConfig);
            setEditNarrationChunkId(null);
          } finally {
            setIsNarrationLoading(false);
          }
          return;
        }

        const ok = await videoEditorStore!.EditChunk(
          organizationId,
          mediaFileId,
          option,
          chunkId,
        );
        if (ok) {
          fetchChunks();
        } else {
          setForceWorking(false);
          forceWorkingUntilRef.current = 0;
          pendingDeleteIdRef.current = null;
        }
      };

      const onSelectStoryblocksMedia = (
        storyBlocksVideoId: string,
        chunkId: string,
      ) => {
        setShowBrowseStoryblocksForChunkId(null);
        setForceWorking(true);
        forceWorkingUntilRef.current = Date.now() + 20000;
        videoEditorStore!
          .EditChunk(
            organizationId!,
            mediaFileId!,
            EditChunkOption.ChangeMedia,
            chunkId,
            {storyBlocksVideoId},
          )
          .then(result => {
            if (result) fetchChunks();
            else {
              setForceWorking(false);
              forceWorkingUntilRef.current = 0;
            }
          });
      };

      const onChunksReorder = async (
        chs: GeneratedVideoChunk[],
        activeChunkId: string,
        newIndex: number,
      ) => {
        if (lockedChunkIds.has(activeChunkId)) return;
        const newChunks = chs.map((chunk, index) => ({...chunk, order: index}));
        if (organizationId && mediaFileId) {
          setForceWorking(true);
          forceWorkingUntilRef.current = Date.now() + 20000;
          try {
            const result = await videoEditorStore!.EditChunk(
              organizationId,
              mediaFileId,
              EditChunkOption.Reorder,
              activeChunkId,
              {newOrder: newIndex},
            );
            if (result) {
              await fetchChunks();
            }
            setChunks([...newChunks]);
          } finally {
            setForceWorking(false);
            forceWorkingUntilRef.current = 0;
          }
        }
      };

      const revertUnsavedChanges = () =>
        videoEditorStore!
          .revertChanges(organizationId!, mediaFileId!)
          .then(() => {
            setNewChunkIds(new Set());
            setForceWorking(false);
            forceWorkingUntilRef.current = 0;
            pendingDeleteIdRef.current = null;
            setIsAddChunkPending(false);
            fetchChunks();
          });

      const saveUnsavedChanges = () =>
        videoEditorStore!
          .saveChanges(organizationId!, mediaFileId!)
          .then(() => {
            setNewChunkIds(new Set());
            setForceWorking(false);
            forceWorkingUntilRef.current = 0;
            pendingDeleteIdRef.current = null;
            setIsAddChunkPending(false);
            fetchChunks();
          });

      const onNarrationSave = async (newText: string) => {
        if (!editNarrationChunkId || !organizationId || !mediaFileId) return;

        // Keep reference to currently selected chunk to restore after refresh
        const currentSelectedChunkId = selectedChunk?.id;

        setForceWorking(true);
        forceWorkingUntilRef.current = Date.now() + 20000;

        setIsNarrationSaving(true);
        try {
          const success = await videoEditorStore!.updateNarration(
            organizationId,
            mediaFileId,
            editNarrationChunkId,
            newText,
          );

          if (success) {
            toast.success(VideoEditorText.editNarrationSuccess, toastConfig);
          }
          setEditNarrationChunkId(null);
          setEditNarrationText('');

          // Refresh chunks and restore selection to the same chunk
          const updatedChunks = await videoEditorStore!.getVideoChunks(
            organizationId,
            mediaFileId,
          );
          const sorted = updatedChunks.sort((x, y) =>
            x.order > y.order ? 1 : -1,
          );
          setChunks(sorted);

          // Restore selection to the same chunk
          if (currentSelectedChunkId) {
            const sameChunk = sorted.find(c => c.id === currentSelectedChunkId);
            if (sameChunk) {
              setSelectedChunk(sameChunk);
            }
          }
        } catch (error) {
          toast.error('Failed to save narration', toastConfig);
        } finally {
          setIsNarrationSaving(false);
          setForceWorking(false);
          forceWorkingUntilRef.current = 0;
        }
      };

      const onNarrationCancel = () => {
        setEditNarrationChunkId(null);
        setEditNarrationText('');
      };

      const onRemoveBackgroundMusic = async () => {
        if (!organizationId || !mediaFileId || chunks.length === 0) return;
        if (videoEditorStore!.state !== VideoEditorState.Idle) return;

        const ok = await videoEditorStore!.EditChunk(
          organizationId,
          mediaFileId,
          EditChunkOption.DisableBackgroundAudio,
          chunks[0].id,
        );
        if (ok) fetchChunks();
        setShowRemoveBgMusicConfirm(false);
      };

      const canAddMedia = () => {
        if (
          videoEditorStore!.state !== VideoEditorState.Idle ||
          videoEditorStore!.isEditingTemporaryDisabled ||
          isAddChunkPending
        ) {
          toast.info(VideoEditorText.addMediaWait, toastConfig);
          return false;
        }
        return true;
      };

      const openAddFromLibrary = () => {
        if (!canAddMedia()) return;
        setShowAddLibrary(true);
      };

      const openAddFromStoryblocks = () => {
        if (!canAddMedia()) return;
        setShowAddStoryblocks(true);
      };

      const closeAddMediaFlow = () => {
        setShowAddLibrary(false);
        setShowAddStoryblocks(false);
        setPendingAddSourceUrl(null);
        setPendingAddRemoveAudio(false);
        setPendingAddLibraryMediaFileId(null);
        setPendingAddStoryblocksVideoId(null);
      };

      const suppressWorkingOverlay =
        !!showBrowseStoryblocksForChunkId ||
        !!showBrowseLibraryForChunkId ||
        showAddLibrary ||
        showAddStoryblocks ||
        !!pendingAddSourceUrl ||
        (!!replaceAIMediaChunk && !isReplaceAIMediaSaving) ||
        (!!editMediaChunk && !isEditMediaSaving) ||
        (!!editNarrationChunkId && !isNarrationSaving);

      return !mediaFileId ? null : (
        <>
          <VideoEditorUI
            state={videoEditorStore!.state}
            hasUnsavedChanges={videoEditorStore!.hasUnsavedChanges}
            isEditingTemporaryDisabled={
              videoEditorStore!.isEditingTemporaryDisabled
            }
            onRevertUnsavedChanges={revertUnsavedChanges}
            onSaveUnsavedChanges={saveUnsavedChanges}
            onBack={onBack}
            chunks={chunks}
            selectedChunk={selectedChunk}
            isLoading={videoEditorStore!.isLoading}
            onEditChunk={onEditChunk}
            refreshChunks={fetchChunks}
            onChunksReorder={onChunksReorder}
            editChunkErrorCode={videoEditorStore!.editChunkErrorCode}
            clearErrors={() => (videoEditorStore!.editChunkErrorCode = '')}
            onSelectChunk={setSelectedChunk}
            hasBackgroundAudio={videoEditorStore!.hasBackgroundAudio}
            backgroundAudioName={videoEditorStore!.backgroundAudioName}
            onRemoveBackgroundMusic={() => setShowRemoveBgMusicConfirm(true)}
            onAddFromLibrary={openAddFromLibrary}
            onAddFromStoryblocks={openAddFromStoryblocks}
            newChunkIds={newChunkIds}
            forceWorking={forceWorking}
            suppressWorkingOverlay={suppressWorkingOverlay}
            lockedChunkIds={lockedChunkIds}
            scopeTitle={scopeActive ? scopeState?.scopeTitle : undefined}
            scopeAddDisabled={scopeAddDisabled}
            hideChunkDelete={!!scopeState?.fromMicroreels}
          />

          {showBrowseStoryblocksForChunkId && (
            <BrowseStoryblocks
              chunkId={showBrowseStoryblocksForChunkId}
              onCancel={() => setShowBrowseStoryblocksForChunkId(null)}
              onSelect={(storyBlocksVideoId: string, chunkId: string | null) =>
                onSelectStoryblocksMedia(storyBlocksVideoId, chunkId!)
              }
            />
          )}

          {showBrowseLibraryForChunkId && (
            <BrowseMediaLibrary
              organizationId={organizationId}
              onCancel={() => {
                setShowBrowseLibraryForChunkId(null);
                setLibraryDurationFilter(null);
              }}
              onSelect={async (file: MediaFile) => {
                if (
                  !organizationId ||
                  !mediaFileId ||
                  !showBrowseLibraryForChunkId
                )
                  return;

                const libraryRepository = new LibraryRepository();

                const mime = (file.fileType || '').toLowerCase();
                const isImage = mime.includes('image');
                const isVideo = mime.includes('video');

                if (!isImage && !isVideo) {
                  alert('Please select an image or video from Library.');
                  return;
                }

                setShowBrowseLibraryForChunkId(null);
                setLibraryDurationFilter(null);

                setForceWorking(true);
                forceWorkingUntilRef.current = Date.now() + 20000;

                const res = await libraryRepository.changeChunkToLibraryMedia(
                  organizationId,
                  mediaFileId,
                  showBrowseLibraryForChunkId,
                  file.id,
                  isImage,
                );

                if (res?.success) {
                  fetchChunks();
                } else if (res?.errors?.length) {
                  console.error('changeChunkToLibraryMedia error', res.errors);
                  setForceWorking(false);
                  forceWorkingUntilRef.current = 0;
                }
              }}
              mode="media"
              minDurationSeconds={libraryDurationFilter ?? undefined}
            />
          )}

          {showAddLibrary && (
            <BrowseMediaLibrary
              organizationId={organizationId}
              onCancel={() => {
                closeAddMediaFlow();
              }}
              onSelect={async (file: MediaFile) => {
                const mime = (file.fileType || '').toLowerCase();
                const isVideo = mime.includes('video');

                if (!isVideo) {
                  alert('Please select a video from Library.');
                  return false;
                }

                if (!file.fileUrl) {
                  toast.error('Selected file has no preview URL.', toastConfig);
                  return false;
                }

                setPendingAddSourceUrl(file.fileUrl);
                setPendingAddRemoveAudio(false);
                setPendingAddLibraryMediaFileId(file.id);
                setShowAddLibrary(false);
                return true;
              }}
              mode="videos"
            />
          )}

          {showAddStoryblocks && (
            <BrowseStoryblocks
              chunkId={null}
              onCancel={() => setShowAddStoryblocks(false)}
              onSelect={(storyBlocksVideoId: string) => {
                const item = storyblocksStore?.mediaItems.find(
                  m => m.id.toString() === storyBlocksVideoId,
                );
                if (!item) return;
                const previewUrl =
                  item.preview_urls._720p ||
                  item.preview_urls._480p ||
                  item.preview_urls._360p;
                if (!previewUrl) return;

                setPendingAddStoryblocksVideoId(item.id);
                setPendingAddSourceUrl(previewUrl);
                setPendingAddRemoveAudio(false);
                setShowAddStoryblocks(false);
              }}
            />
          )}

          {pendingAddSourceUrl && (
            <CropVideoStepUI
              sourceUrl={pendingAddSourceUrl}
              onCancel={() => {
                closeAddMediaFlow();
              }}
              onSave={(start: number, end: number) => {
                const libraryMediaFileId = pendingAddLibraryMediaFileId;
                const storyBlocksVideoId = pendingAddStoryblocksVideoId;
                const orgId = organizationId;
                const mediaId = mediaFileId;
                const mute = pendingAddRemoveAudio;

                closeAddMediaFlow();

                if (
                  (!libraryMediaFileId && !storyBlocksVideoId) ||
                  !orgId ||
                  !mediaId
                )
                  return;

                setForceWorking(true);
                forceWorkingUntilRef.current = Date.now() + 20000;
                setIsAddChunkPending(true);

                videoEditorStore!
                  .addChunk(orgId, mediaId, {
                    libraryMediaFileId: libraryMediaFileId || undefined,
                    storyBlocksVideoId: storyBlocksVideoId || undefined,
                    start,
                    stop: end,
                    mute,
                  })
                  .then(result => {
                    if (result === 'timeout') {
                      markNewAfterFetchRef.current = true;
                      forceWorkingUntilRef.current = Date.now() + 120000;
                      return;
                    }
                    if (result) {
                      markNewAfterFetchRef.current = true;
                      fetchChunks();
                    } else {
                      setIsAddChunkPending(false);
                      setForceWorking(false);
                      forceWorkingUntilRef.current = 0;
                    }
                  });
              }}
              isLoading={false}
              showRemoveAudio={true}
              removeAudio={pendingAddRemoveAudio}
              onRemoveAudioChange={setPendingAddRemoveAudio}
              removeAudioLabel={VideoEditorText.addMediaRemoveAudio}
              saveLabel="Insert media"
            />
          )}

          <EditMediaModal
            shown={editMediaChunk !== null}
            chunk={editMediaChunk}
            organizationId={organizationId || ''}
            onSave={async (chunkId: string, artifacts: SaveArtifact[]) => {
              if (!organizationId || !mediaFileId) return;
              setIsEditMediaSaving(true);
              try {
                const success = await videoEditorStore!.updateChunkArtifacts(
                  organizationId,
                  mediaFileId,
                  chunkId,
                  artifacts,
                );
                if (success) {
                  toast.success(
                    VideoEditorText.editMediaSaveQueued,
                    toastConfig,
                  );
                }
                setEditMediaChunk(null);
                fetchChunks();
              } catch {
                toast.error(VideoEditorText.editMediaSaveError, toastConfig);
              } finally {
                setIsEditMediaSaving(false);
              }
            }}
            onReplaceAll={async (
              chunkId,
              source,
              libraryMediaFileId,
              storyBlocksVideoId,
              kind,
            ) => {
              if (!organizationId || !mediaFileId) return;
              setIsEditMediaSaving(true);
              try {
                if (source === 'storyblocks' && storyBlocksVideoId !== null) {
                  await videoEditorStore!.EditChunk(
                    organizationId,
                    mediaFileId,
                    EditChunkOption.ChangeMedia,
                    chunkId,
                    {storyBlocksVideoId: String(storyBlocksVideoId)},
                  );
                } else if (source === 'library' && libraryMediaFileId) {
                  await videoEditorStore!.replaceChunkWithLibraryMedia(
                    organizationId,
                    mediaFileId,
                    chunkId,
                    libraryMediaFileId,
                    kind === 'image',
                  );
                }
                setEditMediaChunk(null);
                fetchChunks();
              } finally {
                setIsEditMediaSaving(false);
              }
            }}
            onCancel={() => setEditMediaChunk(null)}
            isLoading={isEditMediaSaving}
          />

          <ReplaceAIMediaModal
            shown={replaceAIMediaChunk !== null}
            chunk={replaceAIMediaChunk}
            organizationId={organizationId || ''}
            onSave={async (chunkId, replacements) => {
              if (!organizationId || !mediaFileId) return;
              setIsReplaceAIMediaSaving(true);
              try {
                const success = await videoEditorStore!.replaceAIMedia(
                  organizationId,
                  mediaFileId,
                  chunkId,
                  replacements,
                );
                if (success) {
                  toast.success(
                    VideoEditorText.replaceAIMediaSuccess,
                    toastConfig,
                  );
                }
                setReplaceAIMediaChunk(null);
                fetchChunks();
              } catch {
                toast.error('Failed to replace AI images', toastConfig);
              } finally {
                setIsReplaceAIMediaSaving(false);
              }
            }}
            onCancel={() => setReplaceAIMediaChunk(null)}
            isLoading={isReplaceAIMediaSaving}
          />

          <EditNarrationModal
            shown={editNarrationChunkId !== null}
            initialText={editNarrationText}
            onSave={onNarrationSave}
            onCancel={onNarrationCancel}
            isLoading={isNarrationSaving}
            isTextLoading={isNarrationLoading}
          />

          <ConfirmationModal
            shown={showRemoveBgMusicConfirm}
            onAccept={onRemoveBackgroundMusic}
            onCancel={() => setShowRemoveBgMusicConfirm(false)}
            acceptText={VideoEditorText.confirmRemoveBackgroundMusicAccept}
            title={VideoEditorText.confirmRemoveBackgroundMusic}
          />
        </>
      );
    },
  ),
);

export default EditVideoSourceContainer;
